import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './CreateCampaignModal.css';

function CreateCampaignModal({ onClose, onCampaignCreated }) {
  const [campaignData, setCampaignData] = useState({
    name: '',
    goalViews: '',
    goalConversions: '',
    startDate: '',
    endDate: '',
    description: '',
    primaryGoal: '',
    avatarType: 'color', // 'color' or 'image'
    avatarColor: '#42bcff', // Default brand color
    avatarImage: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCampaignData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarTypeChange = (type) => {
    setCampaignData(prev => ({
      ...prev,
      avatarType: type,
      avatarImage: type === 'image' ? prev.avatarImage : null
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCampaignData(prev => ({
          ...prev,
          avatarImage: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignData.name.trim()) {
      setError('Please enter a campaign name');
      return;
    }

    if (!campaignData.primaryGoal) {
      setError('Please select a primary campaign goal');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in to create a campaign');

      const campaignRef = doc(db, 'users', user.uid, 'campaigns', campaignData.name.trim());
      await setDoc(campaignRef, {
        ...campaignData,
        name: campaignData.name.trim(),
        goalViews: parseInt(campaignData.goalViews) || 0,
        goalConversions: parseInt(campaignData.goalConversions) || 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        currentViews: 0,
        currentConversions: 0
      });

      if (onCampaignCreated) {
        onCampaignCreated(campaignData.name.trim());
      }
      onClose();
    } catch (err) {
      console.error('Error creating campaign:', err);
      setError(err.message || 'Failed to create campaign');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="create-campaign-modal-overlay">
      <div className="create-campaign-modal">
        <div className="create-campaign-header">
          <h2>Create New Campaign</h2>
          <button className="create-campaign-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        
        <div className="create-campaign-content">
          {/* Campaign Avatar Section */}
          <div className="create-campaign-avatar-section">
            <label className="create-campaign-section-label">Campaign Avatar</label>
            <div className="create-campaign-avatar-options">
              <div className="create-campaign-avatar-preview">
                {campaignData.avatarType === 'color' ? (
                  <div 
                    className="create-campaign-avatar-color"
                    style={{ 
                      background: campaignData.avatarColor.includes('gradient') ? campaignData.avatarColor : undefined,
                      backgroundColor: campaignData.avatarColor.includes('gradient') ? undefined : campaignData.avatarColor
                    }}
                    onClick={() => setShowColorPicker(!showColorPicker)}
                  >
                    {campaignData.name ? campaignData.name.charAt(0).toUpperCase() : 'C'}
                  </div>
                ) : (
                  <div className="create-campaign-avatar-image">
                    {campaignData.avatarImage ? (
                      <img src={campaignData.avatarImage} alt="Campaign avatar" />
                    ) : (
                      <div className="create-campaign-avatar-placeholder">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
                        </svg>
                      </div>
                    )}
                  </div>
                )}
                {campaignData.avatarType === 'color' && (
                  <div className="create-campaign-avatar-hint">Click here</div>
                )}
              </div>
              
              <div className="create-campaign-avatar-controls">
                <div className="create-campaign-avatar-type-selector">
                  <button 
                    className={`create-campaign-avatar-type-btn ${campaignData.avatarType === 'color' ? 'active' : ''}`}
                    onClick={() => handleAvatarTypeChange('color')}
                  >
                    Color
                  </button>
                  <button 
                    className={`create-campaign-avatar-type-btn ${campaignData.avatarType === 'image' ? 'active' : ''}`}
                    onClick={() => handleAvatarTypeChange('image')}
                  >
                    Image
                  </button>
                </div>
                
                {campaignData.avatarType === 'color' && showColorPicker && (
                  <div className="create-campaign-color-picker-dropdown">
                    <div className="create-campaign-preset-colors">
                      <span className="create-campaign-color-label">Choose Color</span>
                      <div className="create-campaign-color-grid">
                        {[
                          { color: '#42bcff', name: 'UGC3 Blue' },
                          { color: '#f8049c', name: 'UGC3 Pink' },
                          { color: '#f97316', name: 'UGC3 Orange' },
                          { color: 'linear-gradient(135deg, #f8049c 0%, #f97316 100%)', name: 'UGC3 Gradient' }
                        ].map((preset) => (
                          <button
                            key={preset.color}
                            className={`create-campaign-preset-color ${campaignData.avatarColor === preset.color ? 'active' : ''}`}
                            style={{ 
                              background: preset.color,
                              backgroundImage: preset.color.includes('gradient') ? preset.color : undefined,
                              backgroundColor: preset.color.includes('gradient') ? undefined : preset.color
                            }}
                            onClick={() => {
                              setCampaignData(prev => ({ ...prev, avatarColor: preset.color }));
                              setShowColorPicker(false);
                            }}
                            title={preset.name}
                          />
                        ))}
                        <div className="create-campaign-custom-color">
                          <input
                            type="color"
                            value={campaignData.avatarColor}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, avatarColor: e.target.value }))}
                            className="create-campaign-color-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {campaignData.avatarType === 'image' ? (
                  <div className="create-campaign-image-upload">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="create-campaign-file-input"
                      id="avatar-upload"
                    />
                    <label htmlFor="avatar-upload" className="create-campaign-upload-btn">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 1L8 15M1 8L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Upload Image
                    </label>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Campaign Details Section */}
          <div className="create-campaign-details-section">
            <div className="create-campaign-form-group">
              <label htmlFor="name" className="create-campaign-label">Campaign Name*</label>
              <input
                type="text"
                id="name"
                name="name"
                value={campaignData.name}
                onChange={handleInputChange}
                placeholder="Enter campaign name"
                disabled={isLoading}
                className="create-campaign-input"
              />
            </div>

            <div className="create-campaign-form-group">
              <label htmlFor="primaryGoal" className="create-campaign-label">Primary Campaign Goal*</label>
              <select
                id="primaryGoal"
                name="primaryGoal"
                value={campaignData.primaryGoal}
                onChange={handleInputChange}
                disabled={isLoading}
                required
                className="create-campaign-select"
              >
                <option value="">Select your primary goal</option>
                <option value="views">Increase Views & Reach</option>
                <option value="engagement">Boost Engagement & Interactions</option>
                <option value="conversions">Drive Conversions & Sales</option>
                <option value="brand_awareness">Build Brand Awareness</option>
                <option value="lead_generation">Generate Leads</option>
                <option value="community">Build Community & Following</option>
              </select>
            </div>

            <div className="create-campaign-goals-row">
              <div className="create-campaign-form-group">
                <label htmlFor="goalViews" className="create-campaign-label">Goal Views</label>
                <input
                  type="number"
                  id="goalViews"
                  name="goalViews"
                  value={campaignData.goalViews}
                  onChange={handleInputChange}
                  placeholder="Enter target views"
                  disabled={isLoading}
                  className="create-campaign-input"
                />
              </div>

              <div className="create-campaign-form-group">
                <label htmlFor="goalConversions" className="create-campaign-label">Goal Conversions</label>
                <input
                  type="number"
                  id="goalConversions"
                  name="goalConversions"
                  value={campaignData.goalConversions}
                  onChange={handleInputChange}
                  placeholder="Enter target conversions"
                  disabled={isLoading}
                  className="create-campaign-input"
                />
              </div>
            </div>

            <div className="create-campaign-dates-row">
              <div className="create-campaign-form-group">
                <label htmlFor="startDate" className="create-campaign-label">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={campaignData.startDate}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="create-campaign-input"
                />
              </div>

              <div className="create-campaign-form-group">
                <label htmlFor="endDate" className="create-campaign-label">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={campaignData.endDate}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="create-campaign-input"
                />
              </div>
            </div>

            <div className="create-campaign-form-group">
              <label htmlFor="description" className="create-campaign-label">Campaign Description</label>
              <textarea
                id="description"
                name="description"
                value={campaignData.description}
                onChange={handleInputChange}
                placeholder="Describe your campaign goals and strategy..."
                disabled={isLoading}
                className="create-campaign-textarea"
                rows="4"
              />
            </div>
          </div>

          {error && <div className="create-campaign-error">{error}</div>}

          <div className="create-campaign-footer">
            <button
              className="create-campaign-cancel-btn"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="create-campaign-create-btn"
              onClick={handleCreateCampaign}
              disabled={isLoading || !campaignData.name.trim() || !campaignData.primaryGoal}
            >
              {isLoading ? 'Creating Campaign...' : 'Create Campaign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateCampaignModal;